import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  FaTable, FaChair, FaCircle, FaSquare, FaCheck, FaUtensils, FaStore,
  FaList, FaClock, FaUser, FaHourglassHalf, FaHotel, FaMoneyBillWave,
  FaTimes
} from 'react-icons/fa';
import { GiParkBench } from 'react-icons/gi';
import { MdRestaurant, MdLocalBar, MdCoffee, MdFastfood } from 'react-icons/md';
import VentanaRevenue from "./VentanaRevenue";
import VentanaListaEspera from "./VentanaListaEspera";
import RevenueRoom from "./RevenueRoom";
import { IP } from "../configuracion.js";
import { API_REVENU_LAYOUT, API_NOMBREMESA, API_REF_LESPERA } from "./config.js";
import "../styles/RevenueSelector.css";
import { useProperty } from "../context/PropertyContext";
import { isOperationalToday, isUpcomingReservation } from "../utils/listaEsperaHelpers";

const svgComponents = {
  'table': FaTable,
  'bench': GiParkBench,
  'chair': FaChair,
  'circle': FaCircle,
  'square': FaSquare,
  'mesa': FaTable,
  'banco': GiParkBench,
  'silla': FaChair,
  'mesa_redonda': FaCircle,
  'mesa_cuadrada': FaSquare,
  'Table': FaTable,
  'Bench': GiParkBench,
  'Chair': FaChair,
  'Circle': FaCircle,
  'Square': FaSquare,
};

const revenueTypeIcons = {
  "Restaurant": MdRestaurant,
  "Bar": MdLocalBar,
  "Cafeteria": MdCoffee,
  "FastFood": MdFastfood,
  "Retail": FaStore,
  "ROOM": FaHotel,
  "Table": FaTable,
  "Ecommerce": FaStore,
  "Default": FaUtensils
};

const revenueTypeColors = {
  "Restaurant": "#3b82f6",
  "Bar": "#8b5cf6",
  "Cafeteria": "#f59e0b",
  "FastFood": "#ef4444",
  "Retail": "#10b981",
  "ROOM": "#8b5cf6",
  "Table": "#3b82f6",
  "Ecommerce": "#10b981",
  "Default": "#6b7280"
};

const RevenueSelector = ({
  revenues,
  onSelectRevenue,
  onChequeClick,
  currentUser,
  propertyConfig,
}) => {
  const { property } = useProperty();
  const [step, setStep] = useState(1);
  const [selectedRevenue, setSelectedRevenue] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [clientCount, setClientCount] = useState("");
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [occupiedSpaces, setOccupiedSpaces] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [waitingList, setWaitingList] = useState([]);
  const [waitingListCount, setWaitingListCount] = useState(0);
  const [showWaitingList, setShowWaitingList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [layoutImage, setLayoutImage] = useState(null);
  const [sections, setSections] = useState([]);
  const [clickedOccupiedSpace, setClickedOccupiedSpace] = useState(null);
  // ✅ FIX doble carga visual: antes, la lista de Revenue se pintaba en
  // cuanto llegaba propertyConfig, y loadAllData (más abajo) recién
  // arrancaba después — su overlay de "Cargando..." (position:absolute,
  // cubre toda la pantalla, ver .rev-loading-overlay) aparecía ENCIMA de
  // la lista que ya se había pintado, y al terminar la destapaba de nuevo
  // ya con las estadísticas de ocupación. Eso se percibía como "cargó,
  // y enseguida volvió a cargar". initialLoadDone retiene la pantalla de
  // carga hasta que loadAllData termina su primera pasada, para que la
  // lista aparezca una sola vez, ya completa. No cambia ningún dato ni
  // ninguna llamada — solo el momento en que se revela el contenido.
  // Las recargas posteriores (después de la primera) siguen mostrando el
  // overlay de "Cargando..." igual que antes.
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [spaceScaleFactor, setSpaceScaleFactor] = useState(1);
  const [containerDimensions, setContainerDimensions] = useState({ width: 800, height: 600 });
  const layoutContainerRef = useRef(null);

  // Estados para ROOM
  const [availableRooms, setAvailableRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [tempSelectedRevenue, setTempSelectedRevenue] = useState(null);

  // Estados para asignación de nombre desde lista de espera (long press en mesa)
  const [longPressSpace, setLongPressSpace] = useState(null);
  const [showWaitingListForSpace, setShowWaitingListForSpace] = useState(false);
  const [pendingCustomerName, setPendingCustomerName] = useState(null);
  const longPressTimer = useRef(null);
  const longPressActivated = useRef(false);
  // Asignación iniciada desde la lista de espera (banner) → el usuario elige la mesa
  const [pendingWaitingAssignment, setPendingWaitingAssignment] = useState(null);

  // Detectar tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (window.innerWidth <= 480) {
        setSpaceScaleFactor(0.7);
      } else if (window.innerWidth <= 768) {
        setSpaceScaleFactor(0.85);
      } else if (window.innerWidth <= 1024) {
        setSpaceScaleFactor(0.95);
      } else {
        setSpaceScaleFactor(1);
      }

      if (layoutContainerRef.current) {
        const { width, height } = layoutContainerRef.current.getBoundingClientRect();
        setContainerDimensions({ width, height });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (layoutContainerRef.current) {
      const { width, height } = layoutContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }
  }, [step, layoutImage]);

  // propertyConfig ahora llega por prop desde App.js (que ya lo carga y
  // cachea en loadPropertyData). Antes este componente volvía a pedir
  // config.json por su cuenta con un fetch() sin caché, duplicando la
  // descarga en cada carga de propiedad. Ver Analisis_Rendimiento_POS.docx,
  // sección 4.1.

  // Función para cargar lista de espera
  const fetchWaitingList = async () => {
    if (!property) return [];

    try {
      const url = `${IP}/tavpos/backend/lista_espera.php?property=${encodeURIComponent(property)}`;
      const response = await fetch(url);

      if (!response.ok) {
        setWaitingList([]);
        setWaitingListCount(0);
        return [];
      }

      const data = await response.json();

      if (data.success) {
        const listData = Array.isArray(data.data) ? data.data : [];
        setWaitingList(listData);
        setWaitingListCount(listData.length);
        return listData;
      } else {
        setWaitingList([]);
        setWaitingListCount(0);
        return [];
      }
    } catch (err) {
      console.error('Error fetching waiting list:', err.message);
      setWaitingList([]);
      setWaitingListCount(0);
      return [];
    }
  };

  // Carga inicial de datos
  // ✅ Protección extra contra doble carga: si este efecto se dispara dos
  // veces seguidas por cualquier motivo (ej. React.StrictMode en desarrollo,
  // que vuelve a ejecutar efectos a propósito para detectar bugs), la
  // corrida vieja se marca `cancelled` y evita pisar el estado con datos
  // obsoletos o volver a mostrar el spinner después de que el nuevo ya
  // terminó. No cambia qué se pide ni el resultado final, solo evita que
  // una corrida vieja actualice la pantalla después de una más nueva.
  useEffect(() => {
    let cancelled = false;

    const loadAllData = async () => {
      if (!propertyConfig || !revenues || revenues.length === 0) {
        // Nada que cargar todavía (o la propiedad no tiene revenues) — no
        // dejar la pantalla de carga trabada esperando algo que no llegará.
        if (!cancelled) setInitialLoadDone(true);
        return;
      }

      if (!cancelled) setLoading(true);
      try {
        const chequesData = await fetchSpaceStatus();
        if (cancelled) return;

        if (propertyConfig?.listadeespera === "Y") {
          await fetchWaitingList();
          if (cancelled) return;
        } else {
          setWaitingList([]);
          setWaitingListCount(0);
        }

        // ✅ OPTIMIZACIÓN: fetchSpaces por cada revenue tipo "Table" es
        // independiente entre sí (ninguno depende del resultado de otro),
        // así que ahora se piden en paralelo con Promise.all en vez de uno
        // por uno con await secuencial. allSpacesPorRevenue conserva el
        // mismo orden que revenues (Promise.all resuelve en orden), así que
        // el arreglo final queda idéntico al que producía el for secuencial.
        const tableRevenues = revenues.filter((revenue) => revenue.Tipo === "Table");
        const allSpacesPorRevenue = await Promise.all(
          tableRevenues.map(async (revenue) => {
            const spacesData = await fetchSpaces(revenue.id);
            return identifyOccupiedSpaces(spacesData, chequesData, revenue.name);
          })
        );
        if (cancelled) return;
        const allSpaces = allSpacesPorRevenue.flat();

        setAvailableSpaces(allSpaces);
      } catch (err) {
        if (!cancelled) console.error('Error loading all spaces:', err);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoadDone(true);
        }
      }
    };

    loadAllData();

    return () => {
      cancelled = true;
    };
  }, [propertyConfig, revenues]);

  const getPropiedadId = () => {
    if (!propertyConfig || !propertyConfig.suc) {
      return null;
    }
    return Number(propertyConfig.suc);
  };

  const normalizeIconType = (iconType) => {
    if (!iconType) return '';
    if (iconType.includes('/')) {
      const baseName = iconType.split('/').pop()?.split('.')[0] || '';
      return baseName.toLowerCase().replace(/[\s_]/g, '-');
    }
    return iconType.toLowerCase().replace(/[\s_]/g, '-');
  };

  const fetchLayout = async (tiendaId) => {
    const propiedadId = getPropiedadId();
    if (!propiedadId || !tiendaId) {
      setLayoutImage(null);
      return;
    }
    try {
      const payload = {
        propiedad_id: propiedadId,
        tienda_id: Number(tiendaId),
        type: 'layout',
        action: 'read',
      };

      const response = await fetch(API_REVENU_LAYOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setLayoutImage(null);
        return;
      }

      const data = await response.json();
      if (data.status === 'success' && data.data?.layout) {
        setLayoutImage(data.data.layout);
      } else {
        setLayoutImage(null);
      }
    } catch (err) {
      console.error('Error al cargar el layout:', err.message);
      setLayoutImage(null);
    }
  };

  const fetchSections = async (tiendaId) => {
    const propiedadId = getPropiedadId();
    if (!propiedadId || !tiendaId) return [];
    try {
      const payload = {
        propiedad_id: propiedadId,
        tienda_id: Number(tiendaId),
        type: 'section',
        action: 'read',
      };
      const response = await fetch(API_REVENU_LAYOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.data?.sections)) {
        setSections(data.data.sections);
        return data.data.sections;
      }
      return [];
    } catch (err) {
      console.error('Error fetching sections:', err.message);
      return [];
    }
  };

  const fetchSpaces = async (tiendaId) => {
    const propiedadId = getPropiedadId();
    if (!propiedadId || !tiendaId) return [];
    try {
      const payload = {
        propiedad_id: propiedadId,
        tienda_id: Number(tiendaId),
        type: 'espacio',
        action: 'read',
      };
      const response = await fetch(API_REVENU_LAYOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      if (data.status === 'success' && data.data?.espaciosBySection) {
        const spaces = Object.values(data.data.espaciosBySection).flat().map(space => ({
          id: space.id,
          name: space.name,
          section: space.Seccion || 'Sin sección',
          x: space.ejex || 0,
          y: space.ejey || 0,
          type: space.icon_type || space.img || '',
          capacity: space.cantidad,
          icon_url: space.img,
          revenueId: Number(tiendaId)
        }));
        return spaces;
      }
      return [];
    } catch (err) {
      console.error('Error fetching spaces:', err.message);
      return [];
    }
  };

  const fetchSpaceStatus = async () => {
    if (!property) return [];
    try {
      const response = await fetch(`${IP}/tavpos/backend/lectura_cheques.php?property=${encodeURIComponent(property)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const data = await response.json();
      if (data.success) {
        const chequesData = Array.isArray(data.data) ? data.data : [];
        setCheques(chequesData);
        return chequesData;
      }
      setCheques([]);
      return [];
    } catch (err) {
      console.error('Error fetching cheques:', err.message);
      setCheques([]);
      return [];
    }
  };

  const identifyOccupiedSpaces = (spaces, chequesData, revenueName) => {
    return spaces.map((space) => {
      const cheque = chequesData.find(
        (c) =>
          c.mesa && c.mesa.toLowerCase() === space.name.toLowerCase() &&
          c.revenue && c.revenue.toLowerCase() === revenueName.toLowerCase() &&
          c.pago_completo === 'Pendiente'
      );
      return {
        ...space,
        occupied: !!cheque,
        clientCount: cheque?.cliente || 0,
        chequeData: cheque
      };
    });
  };

  // Funciones para ROOM
  const getOccupiedRoomsFromCheques = (chequesData, revenue) => {
    if (!chequesData || chequesData.length === 0) {
      return [];
    }

    const roomCheques = chequesData.filter(cheque => {
      const matchesById = cheque.revenue_id === revenue.id;
      const matchesByName = cheque.revenue && cheque.revenue.toLowerCase() === revenue.name.toLowerCase();
      const matchesRevenue = matchesById || matchesByName;
      const isOpen = cheque.pago_completo === 'Pendiente';
      const hasMesa = cheque.mesa && cheque.mesa.trim() !== '';

      return matchesRevenue && isOpen && hasMesa;
    });

    return roomCheques.map(cheque => ({
      id: cheque.id,
      numero_cheque: cheque.numero_cheque,
      roomNumber: cheque.mesa,
      clientCount: cheque.cliente || 1,
      guestName: cheque.nombre_vendedor || cheque.nombre_cajero || 'Sin nombre',
      revenue: cheque.revenue,
      revenue_id: cheque.revenue_id,
      fecha_apertura: cheque.fecha_apertura,
      total_cheque: cheque.total_cheque || 0,
      occupied: true,
      chequeData: cheque
    }));
  };

  const handleRoomConfirm = (roomData) => {
    onSelectRevenue({
      revenue: tempSelectedRevenue,
      space: null,
      clientCount: roomData.clientCount,
      roomNumber: roomData.roomNumber,
      roomInfo: roomData.roomInfo,
    });
    setShowRoomModal(false);
    setTempSelectedRevenue(null);
    setAvailableRooms([]);
  };

  const handleRoomCancel = () => {
    setShowRoomModal(false);
    setTempSelectedRevenue(null);
    setAvailableRooms([]);
  };

  const handleSelectOccupiedRoom = (room) => {
    if (room.chequeData) {
      onChequeClick(room.chequeData);
      setShowRoomModal(false);
      setTempSelectedRevenue(null);
      setAvailableRooms([]);
    }
  };

  // ─── Long press: asignar nombre desde lista de espera ───────────────────────

  const handleSpaceLongPressStart = (space) => {
    console.log('🔴 [LongPress] START:', space.name);
    longPressActivated.current = false;
    longPressTimer.current = setTimeout(() => {
      console.log('🔴 [LongPress] FIRED:', space.name, '| listadeespera:', propertyConfig?.listadeespera, '| waitingList:', waitingList.length);
      longPressActivated.current = true;
      longPressTimer.current = null;
      const isWaitingListEnabled = propertyConfig?.listadeespera === "Y";
      if (!isWaitingListEnabled) return;
      if (waitingList.length === 0) {
        fetchWaitingList().then(list => {
          if (list.length > 0) {
            setLongPressSpace(space);
            setShowWaitingListForSpace(true);
          }
        });
      } else {
        setLongPressSpace(space);
        setShowWaitingListForSpace(true);
      }
    }, 600);
  };

  const handleSpaceLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Cambia el status de un item de la lista de espera (ej. 5 = Sentado)
  const cambiarStatusListaEspera = async (queueId, newStatus) => {
    try {
      const url = `${IP}/tavpos/backend/lista_espera_manage.php?property=${encodeURIComponent(property)}`;
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'change_status', queue_id: queueId, new_status: newStatus })
      });
      // Refrescar lista de espera
      if (fetchWaitingList) await fetchWaitingList();
    } catch (err) {
      console.error('[RevenueSelector] Error cambiando status lista espera:', err);
    }
  };

  // Actualiza SOLO el campo Referencia_Lespera — no toca el campo mesa
  const actualizarRefLespera = async (numeroCheque, referenciaLespera) => {
    const url = `${API_REF_LESPERA}?property=${encodeURIComponent(property)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Property': property },
      body: JSON.stringify({ numeroCheque, referencia_lespera: referenciaLespera })
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (data.success === false) throw new Error(data.message || 'Error actualizando referencia');
    return data;
  };

  const handleAssignWaitingToSpace = async (waitingItem) => {
    if (!longPressSpace || !waitingItem) return;
    const space = longPressSpace;
    const customerName = waitingItem.customer_name || '';

    setShowWaitingListForSpace(false);
    setLongPressSpace(null);

    if (space.occupied && space.chequeData) {
      // Mesa ocupada: guardar nombre en Referencia_Lespera — mesa se queda igual
      try {
        await actualizarRefLespera(space.chequeData.numero_cheque, customerName);
        if (waitingItem.queue_id) await cambiarStatusListaEspera(waitingItem.queue_id, 5);
        const [chequesData, spacesData] = await Promise.all([
          fetchSpaceStatus(),
          fetchSpaces(selectedRevenue.id),
        ]);
        const spacesWithStatus = identifyOccupiedSpaces(spacesData, chequesData, selectedRevenue.name);
        setAvailableSpaces(spacesWithStatus);
        setOccupiedSpaces(spacesWithStatus.filter(s => s.occupied));
      } catch (err) {
        console.error('[RevenueSelector] Error actualizando referencia de mesa:', err);
      }
    } else if (!space.occupied) {
      // Mesa disponible: ir a step 3 con nombre y party_size pre-llenados
      if (waitingItem.queue_id) await cambiarStatusListaEspera(waitingItem.queue_id, 5);
      setSelectedSpace(space);
      setPendingCustomerName(customerName);
      if (waitingItem.party_size) {
        setClientCount(String(waitingItem.party_size));
      }
      setStep(3);
    }
  };

  const handleCloseWaitingListForSpace = () => {
    setShowWaitingListForSpace(false);
    setLongPressSpace(null);
  };

  // ────────────────────────────────────────────────────────────────────────────

  const groupSpacesBySection = (spaces) => {
    return spaces.reduce((acc, space) => {
      const section = space.section || 'Sin sección';
      if (!acc[section]) acc[section] = [];
      acc[section].push(space);
      return acc;
    }, {});
  };

  // Manejo de selección de revenue
  const handleSelectRevenue = (revenue) => {
    if (!revenue || !revenue.id || !revenue.name) {
      return;
    }

    console.log('[RevenueSelector] Revenue seleccionado:', revenue.name);
    console.log('[RevenueSelector] Tipo:', revenue.Tipo);

    const esRoom = revenue.Tipo === "ROOM" || revenue.name === "Room Services";

    // Para ROOM - usar RevenueRoom modal
    if (esRoom) {
      const loadRoomsData = async () => {
        setRoomsLoading(true);
        try {
          const chequesData = await fetchSpaceStatus();
          const occupied = getOccupiedRoomsFromCheques(chequesData, revenue);
          setAvailableRooms(occupied);
          setTempSelectedRevenue(revenue);
          setShowRoomModal(true);
        } catch (err) {
          console.error('[RevenueSelector] Error cargando habitaciones:', err);
        } finally {
          setRoomsLoading(false);
        }
      };
      loadRoomsData();
      return;
    }

    // Para Retail
    if (revenue.Tipo === "Retail") {
      onSelectRevenue({
        revenue: revenue,
        space: null,
        clientCount: 1,
      });
      return;
    }

    // Para Table
    if (revenue.Tipo === "Table") {
      setSelectedRevenue(revenue);
      const loadTableData = async () => {
        try {
          setLoading(true);
          const [chequesData, sectionsData, spacesData] = await Promise.all([
            fetchSpaceStatus(),
            fetchSections(revenue.id),
            fetchSpaces(revenue.id),
          ]);
          await fetchLayout(revenue.id);

          const spacesWithStatus = identifyOccupiedSpaces(spacesData, chequesData, revenue.name);
          setAvailableSpaces(spacesWithStatus);
          setOccupiedSpaces(spacesWithStatus.filter((space) => space.occupied));

          if (propertyConfig?.listadeespera === "Y") {
            await fetchWaitingList();
          }

          if (sectionsData && sectionsData.length > 0) {
            setActiveSection(sectionsData[0]);
          } else if (spacesWithStatus.length > 0) {
            setActiveSection('Sin sección');
          }

          setStep(2);
        } catch (err) {
          console.error('[RevenueSelector] Error cargando datos de tabla:', err.message);
        } finally {
          setLoading(false);
        }
      };
      loadTableData();
      return;
    }

    // Otros tipos
    onSelectRevenue({
      revenue: revenue,
      space: null,
      clientCount: 1,
    });
  };

  const handleSelectSpace = (space) => {
    // Si hay una asignación pendiente desde la lista de espera, ejecutarla
    if (pendingWaitingAssignment) {
      const customerName = pendingWaitingAssignment.customer_name || '';
      const queueId = pendingWaitingAssignment.queue_id;
      if (space.occupied && space.chequeData) {
        actualizarRefLespera(space.chequeData.numero_cheque, customerName)
          .then(async () => {
            if (queueId) await cambiarStatusListaEspera(queueId, 5);
            const [chequesData, spacesData] = await Promise.all([
              fetchSpaceStatus(), fetchSpaces(selectedRevenue.id),
            ]);
            const spacesWithStatus = identifyOccupiedSpaces(spacesData, chequesData, selectedRevenue.name);
            setAvailableSpaces(spacesWithStatus);
            setOccupiedSpaces(spacesWithStatus.filter(s => s.occupied));
          })
          .catch(err => console.error('[RevenueSelector] Error actualizando referencia:', err));
      } else if (!space.occupied) {
        if (queueId) cambiarStatusListaEspera(queueId, 5);
        setSelectedSpace(space);
        setPendingCustomerName(customerName);
        if (pendingWaitingAssignment.party_size) {
          setClientCount(String(pendingWaitingAssignment.party_size));
        }
        setStep(3);
      }
      setPendingWaitingAssignment(null);
      return;
    }

    // Comportamiento normal
    if (space.occupied) {
      if (space.chequeData) {
        setClickedOccupiedSpace(space.id);
        onChequeClick(space.chequeData);
        setTimeout(() => setClickedOccupiedSpace(null), 1000);
      }
      return;
    }
    setSelectedSpace(space);
    setStep(3);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  const handleConfirmSelection = () => {
    const parsedClientCount = parseInt(clientCount, 10);
    if (!parsedClientCount || isNaN(parsedClientCount) || parsedClientCount <= 0) {
      return;
    }

    onSelectRevenue({
      revenue: selectedRevenue,
      space: selectedSpace,
      clientCount: parsedClientCount,
      customerName: pendingCustomerName || null,
    });

    setStep(1);
    setSelectedRevenue(null);
    setSelectedSpace(null);
    setClientCount("");
    setActiveSection(null);
    setAvailableSpaces([]);
    setPendingCustomerName(null);
  };

  const handleKeyPadClick = (key) => {
    if (key === "⌫") {
      setClientCount(prev => prev.slice(0, -1));
    } else if (key === "Confirmar") {
      handleConfirmSelection();
    } else if (key === "Limpiar") {
      setClientCount("");
    } else {
      if (clientCount.length < 5) {
        setClientCount(prev => prev + key);
      }
    }
  };

  const handleWaitingListClick = () => {
    setShowWaitingList(true);
  };

  // Iniciado desde el botón "Mesa" en la lista de espera (banner normal)
  // Guarda el cliente pendiente y cierra el modal — el usuario luego toca una mesa en el layout
  const handleAssignFromWaitingList = (waitingItem) => {
    if (!waitingItem) return;
    setPendingWaitingAssignment(waitingItem);
    setShowWaitingList(false);
  };

  const handleCloseWaitingList = () => {
    setShowWaitingList(false);
  };

  const renderSpaces = () => {
    if (!selectedRevenue || !activeSection || !containerDimensions) return null;

    const grouped = groupSpacesBySection(availableSpaces);
    const spaces = grouped[activeSection] || [];

    if (spaces.length === 0) {
      return (
        <div className="rev-no-spaces-message">
          No hay espacios disponibles en esta sección
        </div>
      );
    }

    const CANVAS_WIDTH = 800;
    const CANVAS_HEIGHT = 600;

    const containerWidth = containerDimensions.width;
    const containerHeight = containerDimensions.height;

    const scaleX = containerWidth / CANVAS_WIDTH;
    const scaleY = containerHeight / CANVAS_HEIGHT;

    const scale = Math.min(scaleX, scaleY);

    const offsetX = (containerWidth - (CANVAS_WIDTH * scale)) / 2;
    const offsetY = (containerHeight - (CANVAS_HEIGHT * scale)) / 2;

    const adjustedSpaces = spaces.map(space => {
      return {
        ...space,
        displayX: offsetX + (space.x * scale),
        displayY: offsetY + (space.y * scale),
      };
    });

    return adjustedSpaces.map((space) => {
      const normalizedIconType = normalizeIconType(space.type);
      const IconComponent = svgComponents[normalizedIconType] ||
                          svgComponents[normalizedIconType.charAt(0).toUpperCase() + normalizedIconType.slice(1)] ||
                          FaTable;

      return (
        <div
          key={space.id}
          className={`rev-space-item ${space.occupied ? 'rev-space-occupied' : 'rev-space-available'} ${clickedOccupiedSpace === space.id ? 'rev-space-clicked' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (longPressActivated.current) {
              longPressActivated.current = false;
              return;
            }
            handleSelectSpace(space);
          }}
          onMouseDown={() => handleSpaceLongPressStart(space)}
          onMouseUp={handleSpaceLongPressEnd}
          onMouseLeave={handleSpaceLongPressEnd}
          onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); handleSpaceLongPressStart(space); }}
          onTouchEnd={handleSpaceLongPressEnd}
          onTouchCancel={handleSpaceLongPressEnd}
          style={{
            left: `${space.displayX}px`,
            top: `${space.displayY}px`,
            transform: `translate(-50%, -50%) scale(${spaceScaleFactor})`,
            position: 'absolute',
            zIndex: 100,
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
          title={`${space.name} ${space.occupied ? `(Ocupada)` : '(Disponible)'} | Toque largo para asignar nombre`}
        >
          <div className="rev-space-content">
            {space.icon_url && (space.icon_url.startsWith('http') || space.icon_url.startsWith('/') || space.icon_url.startsWith('data:')) ? (
              <img
                src={space.icon_url}
                alt={space.name}
                className="rev-space-custom-icon"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              IconComponent && <IconComponent className="rev-space-icon" />
            )}
            <span className="rev-space-name">{space.name}</span>
            {space.chequeData?.referencia_lespera ? (
              <div className="rev-customer-name-badge-tile">
                {space.chequeData.referencia_lespera}
              </div>
            ) : null}
            {space.occupied && space.clientCount > 0 && (
              <div className="rev-client-count-badge">
                {space.clientCount}
              </div>
            )}
            {space.capacity && !space.occupied && (
              <div className="rev-capacity-indicator">
                {space.capacity}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  const renderRevenueList = () => {
    if (!revenues || revenues.length === 0) {
      return (
        <div className="rev-empty-state">
          <div className="rev-empty-icon">🏪</div>
          <p className="rev-empty-text">No hay centros de consumo disponibles</p>
        </div>
      );
    }

    const revenueStats = {};

    revenues.forEach(revenue => {
      revenueStats[revenue.id] = {
        occupied: 0,
        available: 0,
        total: 0,
        totalClients: 0,
      };
    });

    availableSpaces.forEach(space => {
      if (space.revenueId && revenueStats[space.revenueId]) {
        revenueStats[space.revenueId].total++;
        if (space.occupied) {
          revenueStats[space.revenueId].occupied++;
          revenueStats[space.revenueId].totalClients += space.clientCount || 0;
        } else {
          revenueStats[space.revenueId].available++;
        }
      }
    });

    const isWaitingListEnabled = propertyConfig?.listadeespera === "Y";

    // Desglose del contador del banner: "Reservación" cuenta SOLO
    // reservaciones futuras (su fecha todavía no llega); "En Espera"
    // cuenta lo operativo de hoy (walk-ins + reservaciones cuya fecha
    // ya llegó, que se integran solas a la operación del día). Misma
    // regla exacta que usan las pestañas del modal — viven en
    // listaEsperaHelpers.js para que banner y modal nunca se
    // desincronicen.
    const reservaCount = waitingList.filter(isUpcomingReservation).length;
    const enEsperaCount = waitingList.filter(isOperationalToday).length;

    return (
      <div className="rev-revenue-items-list">
        {isWaitingListEnabled && (
          <div
            className={`rev-waiting-list-banner ${waitingListCount === 0 ? 'rev-waiting-list-empty' : ''}`}
            onClick={handleWaitingListClick}
            title={waitingListCount > 0 ? "Ver lista de espera y reservaciones" : "Lista de espera y reservaciones vacía"}
          >
            <div className="rev-waiting-list-content">
              <FaList className="rev-waiting-icon" />
              <span className="rev-waiting-text">Lista de Espera y Reservaciones</span>
              {waitingListCount > 0 ? (
                <span className="rev-waiting-breakdown">
                  <span className="rev-waiting-badge rev-waiting-badge--reserva">
                    {reservaCount} {reservaCount === 1 ? 'Reservación' : 'Reservaciones'}
                  </span>
                  <span className="rev-waiting-badge rev-waiting-badge--espera">
                    {enEsperaCount} En Espera
                  </span>
                </span>
              ) : (
                <span className="rev-waiting-badge rev-badge-empty">0</span>
              )}
              <span className="rev-waiting-arrow">→</span>
            </div>
            {waitingListCount === 0 && (
              <div className="rev-waiting-subtext">Sin personas en espera</div>
            )}
          </div>
        )}

        {revenues.map((revenue, index) => {
          const RevenueIcon = revenueTypeIcons[revenue.Tipo] || revenueTypeIcons.Default;
          const iconColor = revenueTypeColors[revenue.Tipo] || revenueTypeColors.Default;
          const stats = revenueStats[revenue.id] || { occupied: 0, available: 0, total: 0, totalClients: 0 };
          const esRoom = revenue.Tipo === "ROOM" || revenue.name === "Room Services";

          let roomOccupiedCount = 0;
          if (esRoom && cheques.length > 0) {
            const roomCheques = cheques.filter(cheque => {
              const matchesRevenue = cheque.revenue_id === revenue.id ||
                                    (cheque.revenue && cheque.revenue.toLowerCase() === revenue.name.toLowerCase());
              const isOpen = cheque.pago_completo === 'Pendiente';
              const hasMesa = cheque.mesa && cheque.mesa.trim() !== '';
              return matchesRevenue && isOpen && hasMesa;
            });
            roomOccupiedCount = roomCheques.length;
          }

          return (
            <div
              key={revenue.id}
              className={`rev-revenue-list-item ${selectedRevenue?.id === revenue.id ? 'selected' : ''}`}
              onClick={() => handleSelectRevenue(revenue)}
              style={{ '--item-index': index }}
            >
              <div className="rev-revenue-content">
                <div className="rev-revenue-info">
                  <div className="rev-revenue-name">
                    <RevenueIcon style={{ color: iconColor }} />
                    <span title={revenue.name}>{revenue.name}</span>
                    <span className={`rev-revenue-type ${revenue.Tipo?.toLowerCase() || ''}`}>
                      {revenue.Tipo || 'General'}
                    </span>
                    {esRoom && roomOccupiedCount > 0 && (
                      <span className="rev-room-occupied-badge">
                        🏨 {roomOccupiedCount} {roomOccupiedCount === 1 ? 'Pedido a la habitación' : 'Pedidos en las habitaciones'}
                      </span>
                    )}
                  </div>
                  {revenue.description && (
                    <div className="rev-revenue-description">
                      {revenue.description}
                    </div>
                  )}
                </div>

                <div className="rev-revenue-stats">
                  {revenue.Tipo !== "Retail" && !esRoom && revenue.Tipo !== "Ecommerce" ? (
                    <>
                      <div className="rev-stat-item rev-stat-occupied">
                        <span className="rev-stat-dot"></span>
                        <span className="rev-stat-number">{stats.occupied}</span>
                      </div>
                      <div className="rev-stat-item rev-stat-available">
                        <span className="rev-stat-dot"></span>
                        <span className="rev-stat-number">{stats.available}</span>
                      </div>
                      {stats.totalClients > 0 && (
                        <div className="rev-stat-item rev-stat-clients">
                          <span className="rev-stat-icon">👥</span>
                          <span className="rev-stat-number">{stats.totalClients}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rev-stat-item rev-stat-retail">
                      {esRoom ? <FaHotel size={16} /> : <FaStore size={16} />}
                    </div>
                  )}

                  <div className="rev-select-icon">
                    <FaCheck />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStep1Desktop = () => (
    <div className="rev-revenue-list-container">
      <h2 className="rev-revenue-list-title">Selecciona un Centro de Consumo</h2>
      {renderRevenueList()}
    </div>
  );

  const renderStep1Mobile = () => (
    <div className="rev-revenue-list-container">
      <h2 className="rev-revenue-list-title">Centros de Consumo</h2>
      {renderRevenueList()}
    </div>
  );

  const renderLayoutStep = () => {
    // Mesas del usuario actual (solo para Tipo Table)
    const misMesas = (selectedRevenue?.Tipo === 'Table' && currentUser?.nombre && cheques.length > 0)
      ? cheques.filter(c =>
          c.pago_completo === 'Pendiente' &&
          c.revenue && c.revenue.toLowerCase() === selectedRevenue.name.toLowerCase() &&
          c.nombre_vendedor && c.nombre_vendedor.toUpperCase() === currentUser.nombre.toUpperCase()
        )
      : [];

    return (
    <div className="rev-space-selector">
      <div className="rev-space-header">
        <h2>{selectedRevenue.name}</h2>
        <button
          className="rev-close-button"
          onClick={() => { setPendingWaitingAssignment(null); setStep(1); }}
          aria-label="Cerrar"
        >
          ×
        </button>
      </div>

      {pendingWaitingAssignment && (
        <div style={{
          background: '#f59e0b', color: '#fff', padding: '8px 14px',
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: '0.9rem', borderRadius: 6, margin: '6px 8px'
        }}>
          <FaUser />
          <span>Toca una mesa para asignar a <strong>{pendingWaitingAssignment.customer_name}</strong></span>
          <button
            onClick={() => setPendingWaitingAssignment(null)}
            style={{ marginLeft: 'auto', background: 'rgba(0,0,0,0.2)', border: 'none', color: '#fff', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}
          >
            Cancelar
          </button>
        </div>
      )}

      {sections.length > 0 ? (
        <div className="rev-section-tabs">
          {sections.map((section) => (
            <button
              key={section}
              onClick={() => handleSectionChange(section)}
              className={activeSection === section ? 'active' : ''}
            >
              {section}
            </button>
          ))}
        </div>
      ) : (
        <div className="rev-section-tabs">
          <button
            onClick={() => handleSectionChange('Sin sección')}
            className={activeSection === 'Sin sección' ? 'active' : ''}
          >
            Todas las mesas
          </button>
        </div>
      )}

      {/* CONTENEDOR LAYOUT + SIDEBAR */}
      <div className="rev-layout-body">
        <div
          className="rev-layout-container"
          ref={layoutContainerRef}
        >
          {layoutImage ? (
            <div className="rev-layout-image-container">
              <img
                src={layoutImage}
                alt="Layout del restaurante"
                className="rev-layout-image"
                onError={(e) => {
                  console.error('Error cargando imagen:', e);
                  setLayoutImage(null);
                }}
                loading="lazy"
              />
              <div className="rev-spaces-overlay">
                {renderSpaces()}
              </div>
            </div>
          ) : (
            <div className="rev-layout-placeholder">
              <div className="rev-placeholder-content">
                <p>No hay imagen de layout disponible</p>
                <p className="rev-placeholder-sub">Las mesas se muestran en posición por defecto</p>
              </div>
              <div className="rev-spaces-overlay">
                {renderSpaces()}
              </div>
            </div>
          )}
        </div>

        {/* SIDEBAR MIS MESAS */}
        {misMesas.length > 0 && (
          <div className="rev-mis-mesas-sidebar">
            <div className="rev-mis-mesas-title">
              <FaUser className="rev-mis-mesas-icon" />
              <span>Mis Mesas</span>
            </div>
            <div className="rev-mis-mesas-list">
              {misMesas.map((cheque) => (
                <button
                  key={cheque.numero_cheque}
                  className="rev-mis-mesas-item"
                  onClick={() => onChequeClick(cheque)}
                >
                  <span className="rev-mis-mesas-mesa">{cheque.mesa}</span>
                  <span className="rev-mis-mesas-cheque">#{cheque.numero_cheque}</span>
                  {cheque.clientes && (
                    <span className="rev-mis-mesas-clientes">
                      <FaUser style={{ fontSize: '0.65rem' }} /> {cheque.clientes}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="rev-layout-footer">
        <button
          className="rev-back-button"
          onClick={() => setStep(1)}
        >
          ← Volver a centros
        </button>
        <div className="rev-legend">
          <div className="rev-legend-item">
            <div className="rev-legend-color rev-available"></div>
            <span>Disponible</span>
          </div>
          <div className="rev-legend-item">
            <div className="rev-legend-color rev-occupied"></div>
            <span>Ocupada</span>
          </div>
        </div>
      </div>
    </div>
    );
  };

  const renderClientCountStep = () => (
    <div className="rev-client-count-step">
      <div className="rev-client-count-header">
        <h2>Número de Clientes</h2>
        <button
          className="rev-close-button"
          onClick={() => {
            setStep(2);
            setSelectedSpace(null);
            setClientCount("");
            setPendingCustomerName(null);
          }}
        >
          ×
        </button>
      </div>

      <div className="rev-selected-space-info-compact">
        <div className="rev-space-detail-compact">
          <span className="rev-detail-label-compact">Mesa:</span>
          <span className="rev-detail-value-compact">{selectedSpace?.name || "—"}</span>
        </div>
        <div className="rev-space-detail-compact">
          <span className="rev-detail-label-compact">Sector:</span>
          <span className="rev-detail-value-compact">{selectedSpace?.section || "—"}</span>
        </div>
        {selectedSpace?.capacity && (
          <div className="rev-space-detail-compact">
            <span className="rev-detail-label-compact">Capacidad:</span>
            <span className="rev-detail-value-compact">{selectedSpace.capacity} pers.</span>
          </div>
        )}
        {pendingCustomerName && (
          <div className="rev-space-detail-compact rev-customer-name-badge">
            <span className="rev-detail-label-compact">Cliente:</span>
            <span className="rev-detail-value-compact">
              <FaUser style={{ marginRight: 4, fontSize: '0.8em' }} />
              {pendingCustomerName}
            </span>
          </div>
        )}
      </div>

      <div className="rev-client-count-display">
        <div className="rev-count-input-wrapper">
          <input
            type="text"
            value={clientCount}
            readOnly
            placeholder="0"
            className="rev-client-input"
          />
          <span className="rev-count-label">clientes</span>
        </div>
        <div className="rev-count-hint">
          {clientCount === "" ? "Ingresa el número de clientes" : `${clientCount} cliente${parseInt(clientCount) !== 1 ? 's' : ''}`}
        </div>
      </div>

      <div className="rev-numeric-keypad-improved">
        <div className="rev-keypad-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPadClick(key)}
              className="rev-key-improved rev-key-number-improved"
            >
              {key}
            </button>
          ))}
          <button
            onClick={() => handleKeyPadClick("⌫")}
            className="rev-key-improved rev-key-delete-improved"
          >
            ⌫
          </button>
          <button
            onClick={() => handleKeyPadClick(0)}
            className="rev-key-improved rev-key-number-improved"
          >
            0
          </button>
          <button
            onClick={() => handleKeyPadClick("Confirmar")}
            className="rev-key-improved rev-key-confirm-improved"
            disabled={clientCount === "" || parseInt(clientCount) === 0}
          >
            ✓ Confirmar
          </button>
          <button
            onClick={() => handleKeyPadClick("Limpiar")}
            className="rev-key-improved rev-key-clear-improved"
          >
            Limpiar
          </button>
        </div>
      </div>

      <div className="rev-step-buttons">
        <button
          className="rev-back-step-button"
          onClick={() => {
            setStep(2);
            setSelectedSpace(null);
            setClientCount("");
            setPendingCustomerName(null);
          }}
        >
          ← Volver a mesas
        </button>
        <button
          className="rev-cancel-button"
          onClick={() => {
            setStep(1);
            setSelectedRevenue(null);
            setSelectedSpace(null);
            setClientCount("");
            setActiveSection(null);
            setAvailableSpaces([]);
            setPendingCustomerName(null);
          }}
        >
        
          Cancelar
        </button>
      </div>
    </div>
  );

  return (
    <div className="rev-selector-container">
      {/* Overlay de "Cargando..." solo para recargas posteriores a la primera
          (ej. reintentos, cambios de revenues ya con la pantalla visible).
          La primera carga usa la pantalla completa de abajo en su lugar,
          para no tapar contenido recién pintado — ver initialLoadDone. */}
      {loading && initialLoadDone && (
        <div className="rev-loading-overlay">
          <div className="rev-loading-spinner"></div>
          <p>Cargando...</p>
        </div>
      )}

      {error && (
        <div className="rev-error-message">
          <p>{error}</p>
          <button className="rev-close-error" onClick={() => setError(null)}>Cerrar</button>
        </div>
      )}

      {!propertyConfig && property && (
        <div className="rev-loading-config">
          <div className="rev-loading-spinner"></div>
          <p>Cargando configuración...</p>
        </div>
      )}

      {propertyConfig && !initialLoadDone && (
        <div className="rev-loading-config">
          <div className="rev-loading-spinner"></div>
          <p>Cargando centros de consumo...</p>
        </div>
      )}

      {propertyConfig && initialLoadDone && (
        <>
          {step === 1 && (
            <>
              {isMobile ? renderStep1Mobile() : renderStep1Desktop()}
            </>
          )}

          {step === 2 && selectedRevenue && selectedRevenue.Tipo === "Table" && (
            renderLayoutStep()
          )}

          {step === 3 && selectedRevenue && selectedRevenue.Tipo === "Table" && (
            renderClientCountStep()
          )}

          {clickedOccupiedSpace && (
            <VentanaRevenue
              espacio={clickedOccupiedSpace}
              onClose={() => setClickedOccupiedSpace(null)}
            />
          )}

          {showWaitingList && (
            <VentanaListaEspera
              waitingList={waitingList}
              onClose={handleCloseWaitingList}
              onUpdate={fetchWaitingList}
              onAssign={handleAssignFromWaitingList}
              revenues={revenues}
              canAssignToTable={true}
            />
          )}

          {showWaitingListForSpace && (
            <VentanaListaEspera
              waitingList={waitingList}
              onClose={handleCloseWaitingListForSpace}
              onUpdate={fetchWaitingList}
              onAssign={handleAssignWaitingToSpace}
              revenues={revenues}
              assignMode={true}
              assignTargetName={longPressSpace?.name}
            />
          )}

          {showRoomModal && tempSelectedRevenue && (
            <RevenueRoom
              revenue={tempSelectedRevenue}
              property={property}
              onConfirm={handleRoomConfirm}
              onCancel={handleRoomCancel}
              isHotelEnabled={propertyConfig?.ifac?.some(ifacItem => ifacItem.hotel === "Y") || false}
              occupiedRooms={availableRooms}
              onSelectOccupiedRoom={handleSelectOccupiedRoom}
            />
          )}
        </>
      )}
    </div>
  );
};

RevenueSelector.propTypes = {
  revenues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      Tipo: PropTypes.string.isRequired,
      description: PropTypes.string
    })
  ).isRequired,
  onSelectRevenue: PropTypes.func.isRequired,
  onChequeClick: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  propertyConfig: PropTypes.object,
};

export default RevenueSelector;
